import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import ReportLayout, {
  DateRangePicker,
  StatusBadge
} from './components/ReportLayout';
import {
  Users,
  Star,
  DollarSign,
  TrendingUp,
  Calendar,
  UserPlus,
  UserCheck,
  Phone,
  Mail,
  Crown,
  Building2,
  ChevronDown
} from 'lucide-react';

import useCustomerReport from '@/hooks/useCustomerReport';

const PRESET_TO_PERIOD = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month'
};
const TODAY = new Date().toISOString().split('T')[0];

export default function CustomerInsightsReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.CUSTOMERS.SCREEN)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Access Denied
          </h3>
          <p className="text-sm text-slate-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  // Filters
const [startDate, setStartDate] = useState(TODAY);
const [endDate, setEndDate] = useState(TODAY);
const [selectedPreset, setSelectedPreset] = useState('today');
const [minVisits, setMinVisits] = useState('');
const [minSpent, setMinSpent] = useState('');
const [period, setPeriod] = useState(PRESET_TO_PERIOD.today);
const [isCustomDate, setIsCustomDate] = useState(false);

  // Table
  const [sortColumn, setSortColumn] = useState('spentInPeriod');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');

  const exportToCsv = (filename, columns, rows) => {
    if (!rows?.length) return;

    const exportableColumns = columns.filter(
      c => c.key && c.label
    );

    const headers = exportableColumns
      .map(col => `"${col.label.replace(/"/g, '""')}"`)
      .join(',');

    const csvRows = rows.map(row =>
      exportableColumns.map(col => {
        let value = row[col.key];

        if (value === null || value === undefined) return '';
        if (typeof value === 'number') return value;
        if (typeof value === 'string')
          return `"${value.replace(/"/g, '""')}"`;

        return `"${String(value)}"`;
      }).join(',')
    );

    const csvContent = [headers, ...csvRows].join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
const isManualFilter =
  isCustomDate ||
  minVisits !== '' ||
  minSpent !== '';

  // Backend hook
  const {
    data = [],
    summary,
    branchSummary,
    loading,
    error,
    setFilters,
    refreshCustomerInsights
  } = useCustomerReport({
    period,
    startDate,
    endDate,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize
  });

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  // Safe summary
  const safeSummary = {
    totalCustomersInPeriod: summary?.totalCustomersInPeriod ?? summary?.totalCustomers ?? 0,
    newCustomers: summary?.newCustomers ?? 0,
    returningCustomers: summary?.returningCustomers ?? 0,
    totalRevenueFromCustomers: summary?.totalRevenueFromCustomers ?? summary?.totalRevenue ?? 0,
    avgRevenuePerCustomer: summary?.avgRevenuePerCustomer ?? 0,
    avgVisitsPerCustomer: summary?.avgVisitsPerCustomer ?? 0
  };

  const handleExport = (format) => {
    if (format !== 'csv') return;

    exportToCsv(
      `customer-insights-page-${currentPage}.csv`,
      columns,
      data
    );
  };

  // Summary cards
  const summaryCards = useMemo(() => ([
    {
      title: 'Total Customers',
      value: safeSummary.totalCustomersInPeriod.toLocaleString(),
      subtitle: 'Active in period',
      icon: Users
    },
    {
      title: 'New Customers',
      value: safeSummary.newCustomers.toLocaleString(),
      subtitle: 'First-time visitors',
      icon: UserPlus
    },
    {
      title: 'Returning',
      value: safeSummary.returningCustomers.toLocaleString(),
      subtitle: 'Repeat customers',
      icon: UserCheck
    },
    {
      title: 'Total Revenue',
      value: `₹${safeSummary.totalRevenueFromCustomers.toLocaleString()}`,
      subtitle: 'From customers',
      icon: DollarSign
    },
    {
      title: 'Avg per Customer',
      value: `₹${Math.round(safeSummary.avgRevenuePerCustomer).toLocaleString()}`,
      subtitle: 'Average spend',
      icon: TrendingUp
    },
    {
      title: 'Avg Visits',
      value: safeSummary.avgVisitsPerCustomer.toFixed(1),
      subtitle: 'Per customer',
      icon: Calendar
    }
  ]), [safeSummary]);

  // Table columns
  const columns = [
    {
      key: 'rank',
      label: '#',
      width: '60px',
      render: (value) => (
        <div className="flex items-center">
          {value <= 3 ? (
            <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
              <Crown size={14} className="text-amber-600" />
            </div>
          ) : (
            <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">
              {value}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Phone size={10} /> {row.phone}
            </span>
            {row.email && (
              <span className="flex items-center gap-1">
                <Mail size={10} /> {row.email}
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'visitsInPeriod',
      sortable: true,
      label: 'Visits',
      align: 'center',
      render: (v, r) => (
        <div className="text-center">
          <span className="font-semibold">{v}</span>
          <p className="text-xs text-gray-400">{r.lifetimeVisits} total</p>
        </div>
      )
    },
    {
      key: 'spentInPeriod',
      sortable: true,
      label: 'Spent (Period)',
      align: 'right',
      render: v => `₹${v.toLocaleString()}`
    },
    {
      key: 'avgOrderValue',
      sortable: true,
      label: 'Avg Order',
      align: 'right',
      render: v => `₹${v.toLocaleString()}`
    },
    {
      key: 'lifetimeSpent',
      sortable: true,
      label: 'Lifetime Value',
      align: 'right',
      render: v => `₹${v.toLocaleString()}`
    },
    {
      key: 'loyaltyPoints',
      sortable: true,
      label: 'Points',
      align: 'right',
      render: v => (
        <div className="flex items-center justify-end gap-1">
          <Star size={12} className="text-amber-500 fill-amber-500" />
          {v}
        </div>
      )
    },
    {
      key: 'daysSinceLastVisit',
      sortable: true,
      label: 'Last Visit',
      align: 'right',
      render: v => (
        <StatusBadge
          status={v === 0 ? 'Today' : `${v} days ago`}
          type={v <= 7 ? 'success' : v <= 14 ? 'warning' : 'danger'}
        />
      )
    }
  ];

  // Segments
  const vipCustomers = useMemo(() =>
    [...data].sort((a, b) => b.lifetimeSpent - a.lifetimeSpent).slice(0, 3),
    [data]
  );
  const atRiskCustomers = useMemo(() =>
    data.filter(c => c.daysSinceLastVisit >= 7).slice(0, 3),
    [data]
  );

  // Filter UI
  const filters = (
    <div className="space-y-4">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={(preset) => {
          const p = PRESET_TO_PERIOD[preset];

          setSelectedPreset(preset);
          setPeriod(p);
          setIsCustomDate(false);

          setFilters({
            period: p,
            startDate: null,
            endDate: null,
            minVisits,
            minSpent,
            offset: 0,
            limit: pageSize,
          });
        }}
        onStartDateChange={(d) => {
          setStartDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
        onEndDateChange={(d) => {
          setEndDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
      />

      <div className="flex gap-4 pt-4 border-t">
        <input
          placeholder="Min Visits"
          value={minVisits}
          onChange={(e) => setMinVisits(e.target.value)}
          className="px-3 py-2 border rounded-lg w-24"
        />
        <input
          placeholder="Min Spent ₹"
          value={minSpent}
          onChange={(e) => setMinSpent(e.target.value)}
          className="px-3 py-2 border rounded-lg w-32"
        />
        <button
          disabled={!isManualFilter}
          onClick={() => {
            setFilters(
              isCustomDate
                ? {
                    startDate,
                    endDate,
                    minVisits,
                    minSpent,
                    period: null,
                    offset: 0,
                    limit: pageSize,
                  }
                : {
                    period,
                    minVisits,
                    minSpent,
                    startDate: null,
                    endDate: null,
                    offset: 0,
                    limit: pageSize,
                  },
            );
          }}
          className={`px-4 py-2 rounded-lg text-white ${
            isManualFilter
              ? "bg-teal-600 hover:bg-teal-700"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          Apply Filters
        </button>

        {branchOptions.length > 0 && (
          <div className="relative">
            <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={selectedBranchId ?? ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setSelectedBranchId(val);
                setCurrentPage(1);
                setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
              }}
              className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
            >
              <option value="">All Branches</option>
              {branchOptions.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>
    </div>
  );

  // Pagination
  const pagination = {
    currentPage,
    totalItems: safeSummary.totalCustomersInPeriod,
    totalPages: Math.ceil(safeSummary.totalCustomersInPeriod / pageSize),
    pageSize,
    onPageChange: setCurrentPage
  };

  //SORTING
  const sortedData = useMemo(() => {
    if (!data || !sortColumn) return data;

    const sorted = [...data];

    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
      }

      return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
    });

   return sorted;
  }, [data, sortColumn, sortDirection]);

  // CLIENT-SIDE FILTERING - Search across all columns
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || !sortedData) return sortedData;
    const query = searchQuery.toLowerCase().trim();
    return sortedData.filter(row => {
      // Search in customer name
      if (String(row.customerName || '').toLowerCase().includes(query)) return true;
      // Search in phone
      if (String(row.phone || '').toLowerCase().includes(query)) return true;
      // Search in email
      if (String(row.email || '').toLowerCase().includes(query)) return true;
      // Search in numeric fields
      const numericFields = ['rank', 'visitsInPeriod', 'lifetimeVisits', 'spentInPeriod', 'avgOrderValue', 'lifetimeSpent', 'loyaltyPoints', 'daysSinceLastVisit'];
      for (const field of numericFields) {
        if (row[field] !== undefined && row[field] !== null) {
          if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
        }
      }
      return false;
    });
  }, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Customer Insights Report"
      description="Identify top customers for CRM, loyalty programs, and targeted marketing"
      icon={Users}
      exportPermission={P.REPORTS.CUSTOMERS.INSIGHTS_EXPORT}
      category="Customers"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      error={error}
           pagination={{
        currentPage,
        totalPages: 1,
        totalItems: data.length,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
        let nextDirection;

        if (sortColumn === column) {

        nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
        nextDirection = 'desc';
        }
        setSortColumn(column);
        setSortDirection(nextDirection);
      }}

      onRefresh={refreshCustomerInsights}
      onExport={handleExport}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by customer, phone, email..."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <Crown size={16} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">VIP Customers</h3>
              <p className="text-xs text-gray-500">Top 3 by lifetime value</p>
            </div>
          </div>

          <div className="space-y-2">
            {vipCustomers.length === 0 ? (
              <p className="text-sm text-gray-500">No VIP customers</p>
            ) : (
              vipCustomers.map((customer, index) => (
                <div
                  key={customer.customerId}
                  className="flex items-center justify-between p-2 bg-amber-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-amber-200 text-amber-700 rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {customer.customerName}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-amber-700">
                    ₹{customer.lifetimeSpent.toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Calendar size={16} className="text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">At Risk</h3>
              <p className="text-xs text-gray-500">No visit in 7+ days</p>
            </div>
          </div>

          <div className="space-y-2">
            {atRiskCustomers.length === 0 ? (
              <p className="text-sm text-gray-500">No at-risk customers</p>
            ) : (
              atRiskCustomers.map(customer => (
                <div
                  key={customer.customerId}
                  className="flex items-center justify-between p-2 bg-red-50 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {customer.customerName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {customer.daysSinceLastVisit} days ago
                    </p>
                  </div>

                  <button
                    className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                    onClick={() => {
                      console.log('Send offer to', customer.customerId);
                    }}
                  >
                    Send Offer
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <UserPlus size={16} className="text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">New Customers</h3>
              <p className="text-xs text-gray-500">First visit this period</p>
            </div>
          </div>
          <div className="text-center py-4">
            <p className="text-4xl font-bold text-green-600">{safeSummary.newCustomers}</p>
            <p className="text-sm text-gray-500 mt-1">new customers acquired</p>
          </div>
        </div>

      </div>
    </ReportLayout>
  );
}
